import { z } from 'zod';

export const GAMEPAD_MACRO_BUTTONS = {
  A: 4096, B: 8192, X: 16384, Y: 32768, DUP: 1, DDOWN: 2, DLEFT: 4, DRIGHT: 8,
  START: 16, BACK: 32, LS: 64, RS: 128, LB: 256, RB: 512, GUIDE: 1024,
} as const;
const axis = z.number().int().min(-32768).max(32767);
export const gamepadMacroStateSchema = z.object({
  wButtons: z.number().int().min(0).max(65535).refine(value => (value & 0x0800) === 0),
  bLeftTrigger: z.number().int().min(0).max(255),
  bRightTrigger: z.number().int().min(0).max(255),
  sThumbLX: axis, sThumbLY: axis, sThumbRX: axis, sThumbRY: axis,
}).strict();
export type GamepadMacroState = z.infer<typeof gamepadMacroStateSchema>;
const fields = ['wButtons', 'bLeftTrigger', 'bRightTrigger', 'sThumbLX', 'sThumbLY', 'sThumbRX', 'sThumbRY'] as const;

/** Four controller slots; omitted slot retains legacy controller zero. Positive Y is up. */
export class GamepadMacroCodec {
  static neutral(): GamepadMacroState {
    return { wButtons: 0, bLeftTrigger: 0, bRightTrigger: 0, sThumbLX: 0, sThumbLY: 0, sThumbRX: 0, sThumbRY: 0 };
  }
  static isNeutral(state: GamepadMacroState): boolean { return fields.every(field => state[field] === 0); }
  static value(state: GamepadMacroState): string {
    const valid = gamepadMacroStateSchema.parse(state);
    return `STATE:${fields.map(field => valid[field]).join(',')}`;
  }
  static encode(state: GamepadMacroState, delay: number, hold: number, deviceId = 0): string {
    for (const time of [delay, hold]) if (!Number.isInteger(time) || time < 0 || time > 600000) throw new Error('Invalid gamepad timing');
    if (!Number.isInteger(deviceId) || deviceId < 0 || deviceId > 3) throw new Error('Invalid gamepad slot');
    return `gpad=${deviceId ? `${deviceId}:` : ''}${this.value(state)}@${delay}^${hold}`;
  }
  static deviceId(value: string): 0 | 1 | 2 | 3 {
    return /^[0-3]:/.test(value) ? Number(value[0]) as 0 | 1 | 2 | 3 : 0;
  }
  static isState(value: string): boolean { return /^(?:[0-3]:)?STATE:/.test(value); }
  static parseValue(value: string): GamepadMacroState {
    if (!/^STATE:-?\d+(,-?\d+){6}$/.test(value)) throw new Error('Invalid gamepad state');
    const values = value.slice(6).split(',').map(Number);
    return gamepadMacroStateSchema.parse(Object.fromEntries(fields.map((field, index) => [field, values[index]])));
  }
  static control(value: string): GamepadMacroState {
    value = value.replace(/^[0-3]:/, '');
    if (value.startsWith('STATE:')) return this.parseValue(value);
    const state = this.neutral();
    if (value === 'LT') state.bLeftTrigger = 255;
    else if (value === 'RT') state.bRightTrigger = 255;
    else if (Object.prototype.hasOwnProperty.call(GAMEPAD_MACRO_BUTTONS, value))
      state.wButtons = GAMEPAD_MACRO_BUTTONS[value as keyof typeof GAMEPAD_MACRO_BUTTONS];
    else throw new Error('Unknown gamepad macro control');
    return state;
  }
}
